import { useWebsocket } from '@/utils/websocket.util';
import { ExportDocumentWorkerJobMetadata } from '@/store/modules/documents/types/worker-job.types';
import { EVENTS } from '@/store/modules/websocket/websocket.state';
import { useAction, useGetter } from './store.util';
import { saveAs } from 'file-saver';
import {
  DocumentDetail,
  DocumentExportType
} from '../store/modules/documents/types/documents.types';
import Vue from 'vue';
import { DocumentIndexingPayload } from '../jbi-shared/types/search.types';
import { get as _get } from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import { Job, JobId } from 'bull';
import { WorkerJobStatus } from '../jbi-shared/types/cplus-worker.types';

const markWorkerJobAsCompleted = function (this: Vue, payload: Job) {
  return useAction.call(this, 'websocket/markWorkerJobAsCompleted')(payload);
};

const markWorkerJobAsErrored = function (this: Vue, payload: Job) {
  return useAction.call(this, 'websocket/markWorkerJobAsErrored')(payload);
};

const onExportSuccess = async function (
  this: Vue,
  job: Job,
  documentDetail: DocumentDetail | DocumentIndexingPayload,
  type: DocumentExportType
) {
  if (job.returnvalue.status === WorkerJobStatus.PROCESSED) {
    markWorkerJobAsCompleted.call(this, job);

    const { storageUrl } = job.returnvalue
      .metadata as ExportDocumentWorkerJobMetadata;
    const filename = `${
      (documentDetail as DocumentDetail)?.revision?.uniquePublicationId ||
      (documentDetail as DocumentDetail)?.document?.defaultDocDisplayId ||
      (documentDetail as DocumentIndexingPayload)?.uniquePublicationId ||
      (documentDetail as DocumentIndexingPayload)?.defaultDocDisplayId
    } ${
      (documentDetail as DocumentDetail)?.document?.title ||
      (documentDetail as DocumentIndexingPayload)?.title
    }.${type}`;

    Toast.open({
      message: `Downloading .${type} file ...`,
      duration: 3000,
      queue: false
    });
    saveAs(storageUrl, filename);
  } else {
    markWorkerJobAsErrored.call(this, job);
    Toast.open({
      message: `Error exporting. Please try again later.`,
      duration: 3000,
      type: 'is-danger',
      queue: false
    });
  }
};

const onExportError = function (this: Vue, job: Job, error: Error) {
  markWorkerJobAsErrored.call(this, job);

  Toast.open({
    message: `Error exporting. Please try again later.`,
    duration: 3000,
    type: 'is-danger',
    queue: false
  });

  return;
};

export const handleDocumentExporting = async function (
  this: Vue,
  job: Job,
  documentDetail: DocumentDetail | DocumentIndexingPayload,
  type: DocumentExportType
) {
  const { connectToWs, disconnectWs, joinRoom, listenOnceTo } =
    useWebsocket.call(this);
  const { id: jobId } = job;

  await connectToWs();

  Toast.open({
    message: `Preparing .${type} file for download ...`,
    duration: 1000,
    queue: false
  });

  joinRoom(jobId);

  try {
    let event: EVENTS;

    if (type === 'docx') {
      event = EVENTS.EXPORT_DOCUMENT_EVENT;
    } else if (type === 'pdf') {
      event = EVENTS.EXPORT_PDF_EVENT;
    } else {
      throw Error('Invalid export file type');
    }

    job = (await listenOnceTo(event)) as Job;

    await onExportSuccess.call(this, job, documentDetail, type);
    await disconnectWs();
  } catch (error) {
    onExportError.call(this, job, error as Error);
    await disconnectWs();
  }
};
